import React, { useState, useCallback, useMemo } from 'react';

import IconButton from 'components/partials/button/icon-button';
import RadioInput from 'components/partials/radio-input/radio-input';

import EditRangesModal from './edit-ranges-modal';

import {
  AWARD_TYPE_OPTIONS,
  CALCULATED_BY_OPTIONS,
  getRangesLabel,
  formatRangeLabel,
  sortRangesForDisplay,
} from '../definition-tab.constants';

import Icons from 'assets/icons';

import type { Range } from '../definition-tab.constants';

type AcademicRank = { id: string | number; rank: string | number };

type DefinitionTabContentProps = {
  awardType: string;
  calculatedBy: string;
  ranges: Range[];
  academicRanks?: AcademicRank[];
  onAwardTypeChange: (val: string | undefined) => void;
  onCalculatedByChange: (val: string | undefined) => void;
  onRangesChange: (ranges: Range[]) => void;
};

const DefinitionTabContent = ({
  awardType,
  calculatedBy,
  ranges,
  academicRanks = [],
  onAwardTypeChange,
  onCalculatedByChange,
  onRangesChange,
}: DefinitionTabContentProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const displayRanges = useMemo(() => sortRangesForDisplay(ranges), [ranges]);

  const rangesLabel = getRangesLabel(awardType);

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleSaveRanges = useCallback(
    (updatedRanges: Range[]) => {
      onRangesChange(sortRangesForDisplay(updatedRanges));
      setIsModalOpen(false);
    },
    [onRangesChange]
  );

  return (
    <div
      className="flex flex-col gap-3 p-3 flex-1 min-h-0 overflow-y-auto"
      data-testid="definition-tab-content"
    >
      <div className="flex flex-col gap-1.5">
        <span className="text-base font-semibold text-gray-700">
          Award Need based funds using
        </span>
        <div className="flex flex-col items-start gap-0">
          {AWARD_TYPE_OPTIONS.map((option) => (
            <RadioInput
              key={option.value}
              labelFor={`award-type-${option.value}`}
              label={option.label}
              radioValue={option.value}
              value={awardType}
              setAction={onAwardTypeChange}
              onChange={onAwardTypeChange}
              customClass="!mb-0"
            />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-base font-semibold text-gray-700">
          {rangesLabel}
        </span>
        <div className="flex gap-2 items-center flex-wrap">
          {displayRanges.map((range, index) => (
            <span
              key={index}
              className="border border-navy-200 rounded-md px-3 py-1 text-base text-gray-700 bg-white whitespace-nowrap"
              data-testid={`range-tag-${index}`}
            >
              {formatRangeLabel(range)}
            </span>
          ))}
          <IconButton
            variant="reserve-text"
            size="sm"
            icon={<Icons.EditPencil />}
            onClick={handleOpenModal}
            aria-label="Edit ranges"
            data-testid="edit-ranges-button"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-base font-semibold text-gray-700">
          Calculated by
        </span>
        <div className="flex gap-4">
          {CALCULATED_BY_OPTIONS.map((option) => (
            <RadioInput
              key={option.value}
              labelFor={`calculated-by-${option.value}`}
              label={option.label}
              radioValue={option.value}
              value={calculatedBy}
              setAction={onCalculatedByChange}
              onChange={onCalculatedByChange}
              customClass="!mb-0"
            />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-base font-semibold text-gray-700">
          Academic/Scholarship
        </span>
        <div className="flex gap-2 items-center">
          {academicRanks.map((ar) => (
            <span
              key={ar.id}
              className="border border-navy-200 rounded-md px-3 py-1 text-base text-gray-700 bg-white"
              data-testid={`academic-rate-${ar.rank}`}
            >
              {ar.rank}
            </span>
          ))}
        </div>
      </div>

      {isModalOpen && (
        <EditRangesModal
          awardType={awardType}
          ranges={ranges}
          onSave={handleSaveRanges}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default DefinitionTabContent;
